import { c as _c } from "react/compiler-runtime";
import { Popover, PopoverContent } from "@shared-components/shadcn/popover";
import { api } from "@shared-server/api";
import { type AppRouterOutput } from "@shared-server/api/app-router";
import { useDebounce } from "@shared-utils/hooks/use-debounce";
import { useState } from "react";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { listItemVariants } from "@shared-components/list-item";
import { LoaderEllipsis } from "@shared-components/loader-ellipsis";
import { Input } from "@shared-components/shadcn/input";
import { Label } from "@shared-components/shadcn/label";
import { useListKeyNavigation } from "@shared-utils/hooks/use-list-key-navigation";
import { Button } from "@shared-components/shadcn/button";
import { ChevronRight } from "lucide-react";
import { cn } from "@shared-utils/ui";
export type Department = AppRouterOutput["adminDepartments"]["autoCompleteDepartmentNames"][0];
interface DepartmentSelectProps {
  className?: string;
  onSelect: (adUser: Department) => void;
}
const minQueryLength = 3;
export const DepartmentSelect = props => {
  const $ = _c(24);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  let t0;
  if ($[0] !== debouncedQuery) {
    t0 = {
      name: debouncedQuery
    };
    $[0] = debouncedQuery;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = debouncedQuery.length >= minQueryLength;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      enabled: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const searchDepartments = api.adminDepartments.autoCompleteDepartmentNames.useQuery(t0, t2);
  let t3;
  if ($[4] !== searchDepartments.data) {
    t3 = searchDepartments.data ?? [];
    $[4] = searchDepartments.data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const keyListNav = useListKeyNavigation({
    items: t3,
    onEnter: department => {
      if (department) {
        selectItem(department);
      }
    }
  });
  const selectItem = item => {
    props.onSelect(item);
    setQuery("");
  };
  const openPopover = Boolean(query !== "") && query.length >= minQueryLength;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label>{"Search departments"}</Label>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => setQuery(e.target.value);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== keyListNav.onKeyDown || $[9] !== query) {
    t6 = <Input className="w-[200px]" placeholder="Search departments..." onKeyDown={keyListNav.onKeyDown} value={query} onChange={t5} />;
    $[8] = keyListNav.onKeyDown;
    $[9] = query;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setQuery("");
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== searchDepartments.data) {
    t8 = searchDepartments.data ?? [];
    $[12] = searchDepartments.data;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== keyListNav.selectedItem || $[15] !== openPopover || $[16] !== searchDepartments.isLoading || $[17] !== selectItem || $[18] !== t8) {
    t9 = <NestedDepartmentsPopover openPopover={openPopover} onInteractionOutside={t7} isLoading={searchDepartments.isLoading} departments={t8} onSelect={selectItem} selectedItem={keyListNav.selectedItem} />;
    $[14] = keyListNav.selectedItem;
    $[15] = openPopover;
    $[16] = searchDepartments.isLoading;
    $[17] = selectItem;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== props.children || $[21] !== t6 || $[22] !== t9) {
    t10 = <div className="relative">{t4}{t6}{t9}{props.children}</div>;
    $[20] = props.children;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
type NestedPopoverProps = {
  openPopover: boolean;
  onInteractionOutside: () => void;
  isLoading: boolean;
  departments: Department[];
  onSelect: (department: Department) => void;
  selectedItem: Department | undefined;
};
const NestedDepartmentsPopover = props => {
  const $ = _c(39);
  const [useDepartmentChildren, setUseDepartmentChildren] = useState(null);
  const t0 = useDepartmentChildren ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      parentId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(useDepartmentChildren);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data,
    isLoading: isChildrenDepartmentsLoading
  } = api.adminDepartments.getChildrenDepartments.useQuery(t1, t3);
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== data || $[5] !== isChildrenDepartmentsLoading || $[6] !== props || $[7] !== useDepartmentChildren) {
    const listItemClasses = listItemVariants({});
    const isLoading = props.isLoading || isChildrenDepartmentsLoading;
    T1 = Popover;
    t11 = props.openPopover;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PopoverAnchor />;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    T0 = PopoverContent;
    t4 = "start";
    t5 = props.onInteractionOutside;
    t6 = "max-h-[300px] overflow-y-auto p-0";
    t7 = _temp;
    if ($[20] !== isLoading) {
      t8 = isLoading && <div className="p-2 min-w-[100%] min-h-2vh"><LoaderEllipsis className="*:bg-ter-green-400" /></div>;
      $[20] = isLoading;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    if ($[22] !== isLoading || $[23] !== props.departments?.length) {
      t9 = !isLoading && props.departments?.length === 0 && <div className="p-2 text-center text-sm text-muted-foreground">{"Departments not found"}</div>;
      $[22] = isLoading;
      $[23] = props.departments?.length;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t10 = props.departments?.map(department => <div className={cn(listItemClasses, "flex flex-row justify-start gap-1 border-b-1 hover:cursor-pointer")} onClick={() => props.onSelect(department)} key={department?.id}><span className="inline-block flex-4/5">{department.name}</span>{department.hasChildren && <Button variant={department.id === useDepartmentChildren ? "default" : "outline"} size="xs" className="pl-1 flex-1/5" onClick={e_0 => {
        e_0.stopPropagation();
        setUseDepartmentChildren(department.id);
      }}><ChevronRight size={14} /></Button>}{useDepartmentChildren === department.id && <NestedDepartmentsPopover openPopover={Boolean(useDepartmentChildren)} onInteractionOutside={() => setUseDepartmentChildren(null)} isLoading={isLoading} departments={data ?? []} onSelect={props.onSelect} selectedItem={department} />}</div>);
    $[4] = data;
    $[5] = isChildrenDepartmentsLoading;
    $[6] = props;
    $[7] = useDepartmentChildren;
    $[8] = T0;
    $[9] = T1;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[8];
    T1 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t13;
  if ($[25] !== T0 || $[26] !== t10 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t13 = <T0 align={t4} onInteractOutside={t5} className={t6} onOpenAutoFocus={t7}>{t8}{t9}{t10}</T0>;
    $[25] = T0;
    $[26] = t10;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== T1 || $[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t14 = <T1 open={t11}>{t12}{t13}</T1>;
    $[34] = T1;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
};
function _temp(e) {
  return e.preventDefault();
}